import React, { useState, useMemo } from "react";

// ============================================================
// DESIGN SYSTEM (from spec)
// ============================================================
const COLORS = {
  primaryBlue: "#1A4F8A",
  primaryDark: "#0D3366",
  successGreen: "#2E7D32",
  warningAmber: "#F57C00",
  dangerRed: "#C62828",
  backgroundGray: "#F5F7FA",
  mutedText: "#546E7A",
  border: "#B0C4DE",
  white: "#FFFFFF",
};

// ============================================================
// MOCK AUDIT DATA (extends original with more fields)
// ============================================================
const MOCK_AUDIT = [
  {
    id: "AUD-1008",
    timestamp: "2024-04-12T10:30:00",
    camp: "Camp Alpha",
    user: "Admin",
    action: "Bill Generated",
    field: "Bill Amount",
    before: "₹74,750",
    after: "₹78,500",
    reason: "Case correction triggered recalculation – added 4 missing cases from Apr 10",
  },
  {
    id: "AUD-1007",
    timestamp: "2024-04-12T09:10:00",
    camp: "Camp Alpha",
    user: "Admin",
    action: "Case Edit",
    field: "Case Count",
    before: "28",
    after: "32",
    reason: "Missed cases added after camp manager review",
  },
  {
    id: "AUD-1006",
    timestamp: "2024-04-10T14:00:00",
    camp: "Camp Beta",
    user: "Manager",
    action: "Other",
    field: "MG Floor",
    before: "150",
    after: "180",
    reason: "Contract exception approval – MG increased due to volume commitment",
  },
  {
    id: "AUD-1005",
    timestamp: "2024-04-10T09:00:00",
    camp: "Camp Alpha",
    user: "Admin",
    action: "Bill Generated",
    field: "-",
    before: "-",
    after: "Generated",
    reason: "Scheduled monthly billing run",
  },
  {
    id: "AUD-1004",
    timestamp: "2024-04-08T11:40:00",
    camp: "Camp Delta",
    user: "Manager",
    action: "Other",
    field: "Case Rate",
    before: "₹250",
    after: "₹275",
    reason: "Contract amendment – rate increased per addendum #3",
  },
];

// ============================================================
// REUSABLE COMPONENTS
// ============================================================
const ActionBadge = ({ action }) => {
  const map = {
    "Case Edit": { bg: "#FFF3E0", color: COLORS.warningAmber },
    "Bill Generated": { bg: "#E3F2FD", color: COLORS.primaryBlue },
    Other: { bg: "#ECEFF1", color: COLORS.mutedText },
  };
  const { bg, color } = map[action] || map.Other;
  return (
    <span
      style={{
        background: bg,
        color: color,
        padding: "4px 10px",
        borderRadius: "50px",
        fontSize: "11px",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {action}
    </span>
  );
};

const Button = ({ children, onClick, variant = "primary", disabled = false }) => {
  const variants = {
    primary: { background: COLORS.primaryBlue, color: COLORS.white, border: "none", hoverBg: COLORS.primaryDark },
    secondary: { background: "transparent", color: COLORS.primaryBlue, border: `1px solid ${COLORS.primaryBlue}`, hoverBg: "#E3EDF7" },
  };
  const style = variants[variant];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...style,
        padding: "8px 16px",
        borderRadius: "8px",
        fontSize: "13px",
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "all 0.2s",
        height: "36px",
      }}
      onMouseEnter={(e) => !disabled && (e.currentTarget.style.background = style.hoverBg)}
      onMouseLeave={(e) => !disabled && (e.currentTarget.style.background = style.background)}
    >
      {children}
    </button>
  );
};

const Input = ({ label, error, ...props }) => (
  <div style={{ marginBottom: "16px" }}>
    {label && (
      <label style={{ fontSize: "12px", fontWeight: 500, marginBottom: "4px", display: "block", color: COLORS.mutedText }}>
        {label}
      </label>
    )}
    <input
      {...props}
      style={{
        border: `1px solid ${error ? COLORS.dangerRed : COLORS.border}`,
        borderRadius: "6px",
        height: "36px",
        padding: "0 12px",
        fontSize: "13px",
        width: "100%",
        outline: "none",
        ...props.style,
      }}
    />
  </div>
);

const Select = ({ options, value, onChange, label }) => (
  <div style={{ marginBottom: "16px" }}>
    {label && <label style={{ fontSize: "12px", fontWeight: 500, marginBottom: "4px", display: "block", color: COLORS.mutedText }}>{label}</label>}
    <select
      value={value}
      onChange={onChange}
      style={{
        height: "36px",
        padding: "0 12px",
        border: `1px solid ${COLORS.border}`,
        borderRadius: "6px",
        fontSize: "13px",
        background: COLORS.white,
        cursor: "pointer",
        width: "100%",
      }}
    >
      {options.map((opt) => (
        <option key={opt.value || opt} value={opt.value || opt}>
          {opt.label || opt}
        </option>
      ))}
    </select>
  </div>
);

const StatCard = ({ label, value, borderColor }) => (
  <div
    style={{
      background: COLORS.white,
      border: `1px solid ${COLORS.border}`,
      borderLeft: `5px solid ${borderColor}`,
      padding: "18px",
      borderRadius: "10px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    }}
  >
    <div style={{ fontSize: "32px", fontWeight: 700, lineHeight: 1.2 }}>{value}</div>
    <div style={{ fontSize: "12px", marginTop: "8px", color: COLORS.mutedText }}>{label}</div>
  </div>
);

// Helper: format ISO timestamp to "DD MMM YYYY HH:mm"
const formatTimestamp = (isoString) => {
  const d = new Date(isoString);
  return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }) +
    " " +
    d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
};

// Helper: truncate reason to 40 chars, add tooltip with full text
const TruncatedReason = ({ text }) => {
  const maxLen = 40;
  const truncated = text.length > maxLen ? text.slice(0, maxLen) + "…" : text;
  return <span title={text}>{truncated}</span>;
};

// ============================================================
// MAIN AUDIT LOG PAGE
// ============================================================
export default function AuditLogPage() {
  const [search, setSearch] = useState("");
  const [campFilter, setCampFilter] = useState("All");
  const [actionFilter, setActionFilter] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Extract unique camp names for dropdown
  const uniqueCamps = useMemo(() => ["All", ...new Set(MOCK_AUDIT.map((log) => log.camp))], []);

  // Filter logic
  const filteredLogs = useMemo(() => {
    return MOCK_AUDIT.filter((log) => {
      // Search: matches id, camp, user, action, reason
      const searchMatch =
        !search ||
        log.id.toLowerCase().includes(search.toLowerCase()) ||
        log.camp.toLowerCase().includes(search.toLowerCase()) ||
        log.user.toLowerCase().includes(search.toLowerCase()) ||
        log.action.toLowerCase().includes(search.toLowerCase()) ||
        log.reason.toLowerCase().includes(search.toLowerCase());

      // Camp filter
      const campMatch = campFilter === "All" || log.camp === campFilter;

      // Action filter (spec: All, Case Edit, Bill Generated, Other)
      let actionMatch = true;
      if (actionFilter !== "All") {
        if (actionFilter === "Case Edit") actionMatch = log.action === "Case Edit";
        else if (actionFilter === "Bill Generated") actionMatch = log.action === "Bill Generated";
        else if (actionFilter === "Other") actionMatch = !["Case Edit", "Bill Generated"].includes(log.action);
      }

      // Date range filter (timestamp between startDate and endDate)
      let dateMatch = true;
      if (startDate || endDate) {
        const logDate = log.timestamp.split("T")[0]; // YYYY-MM-DD
        if (startDate && logDate < startDate) dateMatch = false;
        if (endDate && logDate > endDate) dateMatch = false;
      }

      return searchMatch && campMatch && actionMatch && dateMatch;
    }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // newest first
  }, [search, campFilter, actionFilter, startDate, endDate]);

  // Summary stats (optional but nice)
  const totalEvents = filteredLogs.length;
  const caseEdits = filteredLogs.filter((l) => l.action === "Case Edit").length;
  const billGenerated = filteredLogs.filter((l) => l.action === "Bill Generated").length;

  // Export CSV handler
  const exportCSV = () => {
    // In real app, generate CSV from filteredLogs
    alert("Export CSV – would download current filtered logs");
  };

  return (
    <div style={{ width: "100%" }}>
      {/* Page Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h1 style={{ fontSize: "24px", fontWeight: 700, margin: 0, color: "#1A2C3E" }}>Audit Log</h1>
          <p style={{ fontSize: "13px", color: COLORS.mutedText, marginTop: "4px" }}>
            Immutable, system‑wide audit trail for all case edits and bill actions
          </p>
        </div>
        <Button variant="secondary" onClick={exportCSV}>Export CSV</Button>
      </div>

      {/* Immutability Compliance Note */}
      <div
        style={{
          background: "#FFF8E1",
          borderLeft: `4px solid ${COLORS.warningAmber}`,
          padding: "12px 16px",
          borderRadius: "8px",
          marginBottom: "20px",
          fontSize: "13px",
          color: "#5D4037",
        }}
      >
        🔒 <strong>Compliance Note:</strong> The audit log is immutable. Rows cannot be edited, deleted, or hidden from any user role. This is enforced at both the UI and API level.
      </div>

      {/* Filters Row */}
      <div
        style={{
          background: COLORS.white,
          border: `1px solid ${COLORS.border}`,
          borderRadius: "10px",
          padding: "20px",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
          <Input
            placeholder="Search (ID, camp, user, action, reason)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            label="Camp"
            value={campFilter}
            onChange={(e) => setCampFilter(e.target.value)}
            options={uniqueCamps}
          />
          <div>
            <label style={{ fontSize: "12px", fontWeight: 500, marginBottom: "4px", display: "block", color: COLORS.mutedText }}>Date Range</label>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ height: "36px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, flex: 1 }}
              />
              <span>–</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ height: "36px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, flex: 1 }}
              />
            </div>
          </div>
        </div>

        {/* Action Type Filter Pills */}
        <div style={{ marginTop: "16px" }}>
          <label style={{ fontSize: "12px", fontWeight: 500, marginBottom: "8px", display: "block", color: COLORS.mutedText }}>Action Type</label>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {["All", "Case Edit", "Bill Generated", "Other"].map((type) => (
              <button
                key={type}
                onClick={() => setActionFilter(type)}
                style={{
                  padding: "6px 14px",
                  borderRadius: "20px",
                  border: `1px solid ${actionFilter === type ? COLORS.primaryBlue : COLORS.border}`,
                  background: actionFilter === type ? COLORS.primaryBlue : "transparent",
                  color: actionFilter === type ? COLORS.white : COLORS.mutedText,
                  fontWeight: actionFilter === type ? 600 : 400,
                  cursor: "pointer",
                  fontSize: "12px",
                  transition: "all 0.2s",
                }}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Optional Summary Stats (not required by spec but adds value) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "20px" }}>
        <StatCard label="Total Events" value={totalEvents} borderColor={COLORS.primaryBlue} />
        <StatCard label="Case Edits" value={caseEdits} borderColor={COLORS.warningAmber} />
        <StatCard label="Bill Generations" value={billGenerated} borderColor={COLORS.successGreen} />
      </div>

      {/* Audit Table */}
      <div style={{ overflowX: "auto", border: `1px solid ${COLORS.border}`, borderRadius: "10px", background: COLORS.white }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", minWidth: "900px" }}>
          <thead style={{ background: COLORS.backgroundGray, borderBottom: `1px solid ${COLORS.border}` }}>
            <tr>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: COLORS.mutedText }}>Timestamp</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: COLORS.mutedText }}>Camp</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: COLORS.mutedText }}>User</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: COLORS.mutedText }}>Action</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: COLORS.mutedText }}>Field</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: COLORS.mutedText }}>Before</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: COLORS.mutedText }}>After</th>
              <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: COLORS.mutedText }}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.map((log, idx) => (
              <tr
                key={log.id}
                style={{
                  borderBottom: idx === filteredLogs.length - 1 ? "none" : `1px solid ${COLORS.border}`,
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#F8FAFE")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>{formatTimestamp(log.timestamp)}</td>
                <td style={{ padding: "12px 16px", fontWeight: 500 }}>{log.camp}</td>
                <td style={{ padding: "12px 16px" }}>{log.user}</td>
                <td style={{ padding: "12px 16px" }}><ActionBadge action={log.action} /></td>
                <td style={{ padding: "12px 16px" }}>{log.field}</td>
                <td style={{ padding: "12px 16px", color: COLORS.dangerRed, fontWeight: 500 }}>{log.before}</td>
                <td style={{ padding: "12px 16px", color: COLORS.successGreen, fontWeight: 500 }}>{log.after}</td>
                <td style={{ padding: "12px 16px" }}>
                  <TruncatedReason text={log.reason} />
                </td>
              </tr>
            ))}
            {filteredLogs.length === 0 && (
              <tr>
                <td colSpan="8" style={{ padding: "48px", textAlign: "center", color: COLORS.mutedText }}>
                  No audit records match your filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}